import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { setUser } from '@sentry/react'
import { useStytchMemberSession, useStytchMember } from '@stytch/react/b2b'
import FeatureFlagProvider from '@/feature_flags/FeatureFlagProvider'
import CurrentAccountProvider from '@/components/accounts/CurrentAccountProvider'
import { NotificationProvider } from '@/components/notifications/NotificationProvider'
import RecentOrganizationsProvider from '@/components/organizations/RecentOrganizationsProvider'

export default function ProtectedRoutes() {
  const { session, fromCache } = useStytchMemberSession()
  const { member } = useStytchMember()
  const { pathname } = useLocation()
  // Links from outside the app (OAuth, and notification emails and pushes
  // through the open route) come back here after signing in.
  const returnsAfterSignIn =
    pathname === '/oauth2/authorize' || pathname.startsWith('/open/')

  useEffect(() => {
    if (import.meta.env.VITE_ENVIRONMENT !== 'prod') return

    if (member) {
      setUser({
        id: member.member_id,
        email: member.email_address,
      })
    } else {
      setUser(null)
    }
  }, [member])

  if (!session && !fromCache) {
    let url = '/'
    if (returnsAfterSignIn) {
      const urlWithoutOrigin = window.location.href.replace(
        window.location.origin,
        '',
      )
      url = `/authenticate?return_to=${encodeURIComponent(urlWithoutOrigin)}`
    }
    return <Navigate to={url} />
  }

  // If fromCache is true, but no session, the Member object is from
  // the cache and a state refresh is in progress. (from docs: https://stytch.com/docs/b2b/sdks/members/get-member)
  if (!session) {
    return null
  }

  return (
    <NotificationProvider>
      <FeatureFlagProvider>
        <CurrentAccountProvider>
          <RecentOrganizationsProvider>
            <Outlet />
          </RecentOrganizationsProvider>
        </CurrentAccountProvider>
      </FeatureFlagProvider>
    </NotificationProvider>
  )
}
